/**
 * Expression Evaluator Utility
 * Evaluates expressions by replacing chip IDs with actual form values
 * @author Muskan Thakur
 */
import { findElementById, getElementDefaultValue } from "./formElementHelpers";

const CHIP_PATTERN_REGEX = /\[([^\]]+)\]/g;

/**
 * Helper function to escape regex special characters
 */
const escapeRegex = (str) => str.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/**
 * Finds the form element ID that corresponds to a workflow variable ID
 */
export const findElementIdByVariableId = (workflowVariableId, formData, hostSection = "subsections") => {
  if (!formData?.[hostSection]) return null;

  for (const subsection of formData[hostSection]) {
    if (!subsection.elements) continue;

    for (const element of subsection.elements) {
      const elementVariableId = element.properties?.workflowVariableId;
      if (elementVariableId === workflowVariableId || String(elementVariableId) === String(workflowVariableId)) {
        return element.id;
      }

      // Check gridElements if element is a row container (2D array: rows and columns)
      if (element.gridElements && Array.isArray(element.gridElements)) {
        for (const row of element.gridElements) {
          if (Array.isArray(row)) {
            for (const gridElement of row) {
              if (gridElement) {
                const gridElementVariableId = gridElement.properties?.workflowVariableId;
                if (gridElementVariableId === workflowVariableId || String(gridElementVariableId) === String(workflowVariableId)) {
                  return gridElement.id;
                }
              }
            }
          }
        }
      }
    }
  }

  return null;
};

/**
 * Evaluates an expression by replacing chip IDs with actual form values
 * Missing values (not entered) fall back to default values, then treated as 0
 * Deleted chips (not found in workflowVariables) are treated as 0
 * Returns 0 for invalid mathematical results (division by zero, NaN, Infinity)
 */
export const evaluateExpression = (expression, formResponses, formData, workflowVariables, hostSection = "subsections") => {
  if (!expression || !formResponses || !formData || !workflowVariables) {
    return 0;
  }

  let evaluableExpression = expression;
  const regex = new RegExp(CHIP_PATTERN_REGEX);
  let match;
  const replacements = [];

  while ((match = regex.exec(expression)) !== null) {
    const chipIdentifier = match[1];
    const variable = workflowVariables.find((v) => String(v.id) === String(chipIdentifier) || v.name === chipIdentifier);

    let value = 0;

    if (variable) {
      const elementId = findElementIdByVariableId(variable.id, formData, hostSection);
      if (elementId) {
        /* First, check if user entered a value (or pre-populated default from Layer 1)*/
        const formValue = formResponses[elementId];
        if (formValue !== undefined && formValue !== null && formValue !== "") {
          const parsedValue = parseFloat(formValue);
          if (!isNaN(parsedValue)) {
            value = parsedValue;
          }
        } else {
          /* Fallback: check element's default value directly (Layer 2 safety net)*/
          const element = findElementById(elementId, formData, hostSection);
          if (element) {
            const defaultVal = getElementDefaultValue(element);
            if (defaultVal !== null && defaultVal !== undefined && defaultVal !== "") {
              const parsedDefault = parseFloat(defaultVal);
              if (!isNaN(parsedDefault)) {
                value = parsedDefault;
              }
            }
          }
        }
      }
    }

    replacements.push({
      pattern: match[0],
      value: value,
    });
  }

  replacements.forEach(({ pattern, value }) => {
    const escapedPattern = escapeRegex(pattern);
    evaluableExpression = evaluableExpression.replace(new RegExp(escapedPattern, "g"), value);
  });

  const result = Function(`"use strict"; return (${evaluableExpression})`)();

  if (typeof result === "number" && !isNaN(result) && isFinite(result)) {
    return result;
  }

  return 0;
};
